import { getCellExternalId } from '../../table/utils/cellIdUtils';
const escapeHtml = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/\r?\n/g, '<br>');
const FORMULA_PREFIX = /^[=+\-@]/;

/**
 * Serializes the selected cells to a TSV grid (tab-separated columns,
 * newline-separated rows) — the format spreadsheet apps expect on paste — and,
 * when any cell supplies `html` via `getCellCopyValue`, a matching HTML table
 * so rich paste targets can keep e.g. hyperlinks.
 *
 * Text is resolved by calling `getCellCopyValue` when provided, falling back to
 * DOM `textContent` of the cell element. Cells not found in the live DOM are
 * looked up in `stagingContainer` (a hidden render area used for virtualized
 * cells), and are left as empty strings only if absent from both.
 *
 * The output is always the full bounding box of the selection: non-selected
 * cells inside the bounding box are serialized as empty strings.
 */
export const serializeSelectionToTsv = (coords, columns, rows, getRowId, getCellCopyValue, stagingContainer) => {
  if (coords.length === 0) return {
    tsv: ''
  };
  const sorted = [...coords].sort((a, b) => a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex);
  const rowGroups = new Map();
  let hasHtml = false;
  sorted.forEach(({
    rowIndex,
    columnIndex
  }) => {
    var _document$getElementB, _element$textContent$, _element$textContent;
    const row = rows[rowIndex];
    const column = columns[columnIndex];
    if (row === undefined || column === undefined) return;
    const externalId = getCellExternalId(column.columnNamespace, column.columnName, getRowId(row));
    const element = (_document$getElementB = document.getElementById(externalId)) !== null && _document$getElementB !== void 0 ? _document$getElementB : stagingContainer ? stagingContainer.querySelector(`[data-fdt-row-index="${rowIndex}"][data-column-index="${columnIndex}"]`) : null;
    if (!element) return;
    const value = getCellCopyValue ? getCellCopyValue({
      columnName: column.columnName,
      columnNamespace: column.columnNamespace,
      row,
      element
    }) : (_element$textContent$ = (_element$textContent = element.textContent) === null || _element$textContent === void 0 ? void 0 : _element$textContent.trim()) !== null && _element$textContent$ !== void 0 ? _element$textContent$ : '';
    const {
      text: raw,
      html: rawHtml
    } = typeof value === 'string' ? {
      text: value,
      html: undefined
    } : value;
    // Prefix with a single quote to prevent spreadsheet apps (Excel, Google
    // Sheets) from interpreting values starting with =, +, -, or @ as formulas.
    // The quote signals "treat this as a string literal" on paste.
    const isFormulaLike = FORMULA_PREFIX.test(raw);
    const text = isFormulaLike ? `'${raw}` : raw;
    // RFC 4180 quoting: wrap fields containing structural characters (\r, \n,
    // \t, ") in double quotes and escape internal double quotes as "".
    // Spreadsheet apps treat quoted fields as a single cell even when the value
    // spans multiple lines or contains tabs.
    const sanitized = /[\r\n\t"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    // Consumer html can't be prefixed, so a formula-like value drops its rich
    // content and pastes as the guarded plain text instead.
    const useConsumerHtml = rawHtml !== undefined && !isFormulaLike;
    if (useConsumerHtml) hasHtml = true;
    const html = useConsumerHtml ? rawHtml : escapeHtml(text);
    if (!rowGroups.has(rowIndex)) {
      rowGroups.set(rowIndex, []);
    }
    rowGroups.get(rowIndex).push({
      columnIndex,
      text: sanitized,
      html
    });
  });
  if (rowGroups.size === 0) return {
    tsv: ''
  };
  const minRow = sorted[0].rowIndex;
  const maxRow = sorted[sorted.length - 1].rowIndex;
  const minCol = sorted.reduce((min, c) => Math.min(min, c.columnIndex), sorted[0].columnIndex);
  const maxCol = sorted.reduce((max, c) => Math.max(max, c.columnIndex), sorted[0].columnIndex);
  const lines = [];
  const htmlRows = [];
  for (let row = minRow; row <= maxRow; row++) {
    const rowCells = rowGroups.get(row);
    const cellMap = new Map();
    if (rowCells) {
      rowCells.forEach(c => cellMap.set(c.columnIndex, c));
    }
    const cells = [];
    const htmlCells = [];
    for (let col = minCol; col <= maxCol; col++) {
      var _cell$text, _cell$html;
      const cell = cellMap.get(col);
      cells.push((_cell$text = cell === null || cell === void 0 ? void 0 : cell.text) !== null && _cell$text !== void 0 ? _cell$text : '');
      htmlCells.push(`<td>${(_cell$html = cell === null || cell === void 0 ? void 0 : cell.html) !== null && _cell$html !== void 0 ? _cell$html : ''}</td>`);
    }
    lines.push(cells.join('\t'));
    htmlRows.push(`<tr>${htmlCells.join('')}</tr>`);
  }
  return {
    tsv: lines.join('\n'),
    html: hasHtml ? `<table><tbody>${htmlRows.join('')}</tbody></table>` : undefined
  };
};